import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, PhotoIcon, CodeBracketIcon, RectangleGroupIcon, TagIcon } from '@heroicons/react/24/outline';
import { Button, Input, Select, Card, Loading, Badge } from '../../components/Common';
import api from '../../services/api';

const AdvertisementForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [adTypes, setAdTypes] = useState([]);
  const [adTypesRaw, setAdTypesRaw] = useState([]);
  const [adFormats, setAdFormats] = useState([]);
  const [adFormatsRaw, setAdFormatsRaw] = useState([]);
  const [adSlots, setAdSlots] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    slot: '',
    type_id: 1,
    format_id: 3,
    imageUrl: '',
    targetUrl: '',
    htmlContent: '',
    adsenseSlotId: '',
    startDate: '',
    endDate: '',
    isActive: true,
    priority: 0,
    targetPages: [],
    targetCategories: [],
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadMode, setUploadMode] = useState('url');
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [youtubeUrl, setYoutubeUrl] = useState('');

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await api.get('/advertisements/config/options');
        const data = response.data.data;
        setAdTypesRaw(data.types || []);
        setAdTypes(data.types.map(t => ({ value: t.id, label: t.name })));
        setAdFormatsRaw(data.formats || []);
        setAdFormats(data.formats.map(f => ({
          value: f.id,
          label: f.width && f.height ? `${f.name} (${f.width}x${f.height})` : f.name
        })));
        setAdSlots(data.slots.map(s => ({ value: s.key, label: s.label })));
      } catch (error) {
        console.warn('Failed to fetch ad config from API, using defaults:', error);
        const defaultTypes = [
          { id: 1, name: 'Banner', description: 'Standard banner ad' },
          { id: 2, name: 'Sidebar', description: 'Sidebar placement ad' },
          { id: 3, name: 'In-Article', description: 'Embedded within article content' },
          { id: 4, name: 'Popup', description: 'Popup/overlay advertisement' },
          { id: 5, name: 'Native', description: 'Native/content-matched ad' },
          { id: 6, name: 'Video', description: 'Video advertisement with YouTube embed' },
          { id: 7, name: 'Interstitial', description: 'Full-screen interstitial ad' },
        ];
        const defaultFormats = [
          { id: 1, name: 'Vertical', width: 160, height: 600 },
          { id: 2, name: 'Rectangle', width: 300, height: 250 },
          { id: 3, name: 'Auto', width: null, height: null },
          { id: 4, name: 'Leaderboard', width: 728, height: 90 },
          { id: 5, name: 'Square', width: 250, height: 250 },
          { id: 6, name: 'Wide Skyscraper', width: 160, height: 600 },
          { id: 7, name: 'Half Page', width: 300, height: 600 },
          { id: 8, name: 'Large Rectangle', width: 336, height: 280 },
          { id: 9, name: 'Billboard', width: 970, height: 250 },
          { id: 10, name: 'Portrait', width: 300, height: 1050 },
        ];
        setAdTypesRaw(defaultTypes);
        setAdTypes(defaultTypes.map(t => ({ value: t.id, label: t.name })));
        setAdFormatsRaw(defaultFormats);
        setAdFormats(defaultFormats.map(f => ({
          value: f.id,
          label: f.width && f.height ? `${f.name} (${f.width}x${f.height})` : f.name
        })));
        setAdSlots([
          { value: 'home-top-banner', label: 'Home Top Banner' },
          { value: 'home-mid-banner', label: 'Home Mid Banner' },
          { value: 'sidebar-top', label: 'Sidebar Top' },
          { value: 'sidebar-bottom', label: 'Sidebar Bottom' },
          { value: 'article-mid', label: 'Article Mid' },
          { value: 'article-bottom', label: 'Article Bottom' },
          { value: 'sidebanner', label: 'Sidebanner' },
          { value: 'footer-banner', label: 'Footer Banner' },
        ]);
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await api.get('/categories/dropdown');
        setCategoryOptions(response.data.data || []);
      } catch (error) {
        setCategoryOptions([]);
      }
    };
    fetchOptions();
    fetchCategories();
  }, []);

  const targetPageOptions = [
    { value: 'home', label: 'Home Page' },
    { value: 'articles', label: 'Articles List' },
    { value: 'article-detail', label: 'Article Detail' },
    { value: 'courses', label: 'Courses' },
    { value: 'universities', label: 'Universities' },
    { value: 'about', label: 'About Page' },
    { value: 'contact', label: 'Contact Page' },
  ];

  useEffect(() => {
    if (id) {
      fetchAdvertisement();
    }
  }, [id]);

  const fetchAdvertisement = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/advertisements/${id}`);
      const ad = response.data.data;
      setFormData({
        name: ad.name || '',
        slot: ad.slot || '',
        type_id: ad.type_id || 1,
        format_id: ad.format_id || 3,
        imageUrl: ad.image_url || '',
        targetUrl: ad.target_url || '',
        htmlContent: ad.html_content || '',
        adsenseSlotId: ad.adsense_slot_id || '',
        startDate: ad.start_date ? ad.start_date.split('T')[0] : '',
        endDate: ad.end_date ? ad.end_date.split('T')[0] : '',
        isActive: ad.is_active !== false,
        priority: ad.priority || 0,
        targetPages: Array.isArray(ad.target_pages) ? ad.target_pages : (ad.target_pages ? JSON.parse(ad.target_pages) : []),
        targetCategories: Array.isArray(ad.target_categories) ? ad.target_categories : (ad.target_categories ? JSON.parse(ad.target_categories) : []),
      });
      if (ad.image_url) {
        setImagePreview(ad.image_url);
      }
      if (ad.html_content) {
        const youtubeMatch = ad.html_content.match(/src="https:\/\/www\.youtube\.com\/embed\/([a-zA-Z0-9_-]+)"/);
        if (youtubeMatch) {
          setYoutubeUrl(`https://www.youtube.com/watch?v=${youtubeMatch[1]}`);
        }
      }
    } catch (error) {
      console.error('Error fetching advertisement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const handleFileSelect = async (e) => {
    if (!e || !e.target || !e.target.files) return;
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, imageUrl: 'Please select an image file' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, imageUrl: 'Image size must be less than 5MB' }));
      return;
    }

    setSelectedFile(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    await handleFileUpload(file);
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    setErrors(prev => ({ ...prev, imageUrl: null }));

    try {
      const fd = new FormData();
      fd.append('image', file);

      const response = await api.post('/advertisements/upload-image', fd);

      if (response.data.success) {
        setFormData(prev => ({ ...prev, imageUrl: response.data.data.imageUrl }));
        setImagePreview(response.data.data.imageUrl);
        setUploadMode('url');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      setErrors(prev => ({
        ...prev,
        imageUrl: error.response?.data?.message || 'Failed to upload image'
      }));
      setSelectedFile(null);
      setImagePreview(null);
    } finally {
      setUploading(false);
    }
  };

  const handleUrlChange = (e) => {
    if (!e || !e.target) return;
    const url = e.target.value;
    setFormData(prev => ({ ...prev, imageUrl: url }));
    setImagePreview(url || null);
    if (errors.imageUrl) setErrors(prev => ({ ...prev, imageUrl: null }));
  };

  const handleTargetPageToggle = (page) => {
    setFormData(prev => ({
      ...prev,
      targetPages: prev.targetPages.includes(page)
        ? prev.targetPages.filter(p => p !== page)
        : [...prev.targetPages, page]
    }));
  };

  const handleTargetCategoryToggle = (catId) => {
    setFormData(prev => ({
      ...prev,
      targetCategories: prev.targetCategories.includes(catId)
        ? prev.targetCategories.filter(c => c !== catId)
        : [...prev.targetCategories, catId]
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.slot) newErrors.slot = 'Slot is required';
    if (!formData.imageUrl && !formData.htmlContent && !formData.adsenseSlotId) {
      newErrors.imageUrl = 'Image URL, HTML content, or AdSense slot ID is required';
    }
    const priority = parseInt(formData.priority);
    if (isNaN(priority) || priority < 0 || priority > 100) {
      newErrors.priority = 'Priority must be between 0 and 100';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getYoutubeEmbedHtml = (url) => {
    if (!url) return '';
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/);
    if (!match) return '';
    return `<div class="ad-youtube-embed" style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;max-width:100%;"><iframe src="https://www.youtube.com/embed/${match[1]}" frameborder="0" allowfullscreen style="position:absolute;top:0;left:0;width:100%;height:100%;"></iframe></div>`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      let finalHtmlContent = formData.htmlContent || '';
      const youtubeEmbed = getYoutubeEmbedHtml(youtubeUrl);
      if (youtubeEmbed) {
        finalHtmlContent = youtubeEmbed + (finalHtmlContent ? '\n' + finalHtmlContent : '');
      }

      const payload = {
        name: formData.name,
        slot: formData.slot,
        type_id: parseInt(formData.type_id),
        format_id: parseInt(formData.format_id),
        imageUrl: formData.imageUrl,
        targetUrl: formData.targetUrl,
        htmlContent: finalHtmlContent,
        adsenseSlotId: formData.adsenseSlotId,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
        isActive: formData.isActive,
        priority: parseInt(formData.priority) || 0,
        targetPages: formData.targetPages,
        targetCategories: formData.targetCategories,
      };

      if (isEdit) {
        await api.put(`/advertisements/${id}`, payload);
      } else {
        await api.post('/advertisements', payload);
      }
      navigate('/advertisements');
    } catch (error) {
      console.error('Error saving advertisement:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Loading fullScreen text="Loading advertisement..." />;

  return (
    <div className="space-y-6 animate-fadeIn max-w-4xl mx-auto">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/advertisements')} className="p-2 hover:bg-slate-100 rounded-lg">
          <ArrowLeftIcon className="w-5 h-5 text-slate-600" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {isEdit ? 'Edit Advertisement' : 'Create Advertisement'}
          </h1>
          <p className="text-slate-500">
            {isEdit ? 'Update advertisement settings' : 'Add a new advertisement placement'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card title="Basic Information" icon={PhotoIcon}>
              <div className="space-y-4">
                <Input
                  label="Advertisement Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Homepage Banner Ad"
                  error={errors.name}
                />
                <div className="grid grid-cols-2 gap-4">
                  <Select
                    label="Ad Slot"
                    name="slot"
                    value={formData.slot}
                    onChange={handleChange}
                    options={adSlots}
                    placeholder="Select slot"
                    error={errors.slot}
                  />
                  <Select
                    label="Ad Type"
                    name="type_id"
                    value={formData.type_id}
                    onChange={handleChange}
                    options={adTypes}
                  />
                  {formData.type_id && (() => {
                    const tp = adTypesRaw.find(t => t.id === parseInt(formData.type_id));
                    if (!tp || !tp.description) return null;
                    return (
                      <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-md border border-slate-200">
                        <TagIcon className="w-4 h-4 text-slate-400" />
                        <span>{tp.description}</span>
                      </div>
                    );
                  })()}
                </div>
                <Select
                  label="Ad Format"
                  name="format_id"
                  value={formData.format_id}
                  onChange={handleChange}
                  options={adFormats}
                />
                {formData.format_id && (() => {
                  const fmt = adFormatsRaw.find(f => f.id === parseInt(formData.format_id));
                  if (!fmt) return null;
                  return (
                    <div className="flex items-center gap-2 text-sm text-slate-600 bg-slate-50 px-3 py-2 rounded-md border border-slate-200">
                      <RectangleGroupIcon className="w-4 h-4 text-slate-400" />
                      <span className="font-medium">{fmt.name}</span>
                      {fmt.width && fmt.height ? (
                        <span className="text-slate-500">— {fmt.width} × {fmt.height} px</span>
                      ) : (
                        <span className="text-slate-500 italic">Responsive / Auto</span>
                      )}
                    </div>
                  );
                })()}
              </div>
            </Card>

            <Card title="Ad Content" icon={CodeBracketIcon}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image Source
                  </label>
                  <div className="flex gap-4 mb-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="uploadMode"
                        value="url"
                        checked={uploadMode === 'url'}
                        onChange={(e) => setUploadMode(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Image URL</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="uploadMode"
                        value="file"
                        checked={uploadMode === 'file'}
                        onChange={(e) => setUploadMode(e.target.value)}
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">Upload File</span>
                    </label>
                  </div>
                </div>

                {uploadMode === 'url' && (
                  <Input
                    label="Image URL"
                    name="imageUrl"
                    value={formData.imageUrl}
                    onChange={handleUrlChange}
                    placeholder="https://example.com/ad-image.jpg"
                    error={errors.imageUrl}
                  />
                )}

                {uploadMode === 'file' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Upload Image
                    </label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
                      <div className="space-y-1 text-center">
                        <PhotoIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <div className="flex text-sm text-gray-600">
                          <label className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                            <span>Upload a file</span>
                            <input
                              type="file"
                              className="sr-only"
                              accept="image/*"
                              onChange={handleFileSelect}
                              disabled={uploading}
                            />
                          </label>
                          <p className="pl-1">or drag and drop</p>
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 5MB
                        </p>
                        {uploading && (
                          <p className="text-sm text-blue-600 font-medium">Uploading...</p>
                        )}
                        {selectedFile && !uploading && (
                          <p className="text-sm text-green-600 font-medium">✓ {selectedFile.name}</p>
                        )}
                      </div>
                    </div>
                    {errors.imageUrl && (
                      <p className="mt-1 text-sm text-red-600">{errors.imageUrl}</p>
                    )}
                  </div>
                )}

                {imagePreview && (
                  <div className="border rounded-lg p-4 bg-gray-50">
                    <p className="text-xs text-gray-500 mb-2">Preview:</p>
                    <img
                      src={imagePreview}
                      alt="Ad preview"
                      className="max-h-40 mx-auto rounded"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        setErrors(prev => ({ ...prev, imageUrl: 'Invalid image URL' }));
                      }}
                    />
                  </div>
                )}

                <Input
                  label="Target URL (Click destination)"
                  name="targetUrl"
                  value={formData.targetUrl}
                  onChange={handleChange}
                  placeholder="https://example.com/landing-page"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Custom HTML Content (optional)
                  </label>
                  <textarea
                    name="htmlContent"
                    value={formData.htmlContent}
                    onChange={handleChange}
                    rows={4}
                    placeholder="<div>Custom ad HTML...</div>"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                </div>
                <Input
                  label="Google AdSense Slot ID (optional)"
                  name="adsenseSlotId"
                  value={formData.adsenseSlotId}
                  onChange={handleChange}
                  placeholder="ca-pub-xxxxxxxx"
                />
                {parseInt(formData.type_id) === 6 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      YouTube Video URL (for Video ads)
                    </label>
                    <input
                      type="url"
                      value={youtubeUrl}
                      onChange={(e) => setYoutubeUrl(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {youtubeUrl && (
                      <p className="mt-1 text-xs text-gray-500">
                        A YouTube embed player will be generated from this URL and included in the ad rendering.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </div>

          <div className="space-y-6">
            <Card title="Status & Schedule">
              <div className="space-y-4">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-5 h-5"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">Active</span>
                    <p className="text-xs text-gray-500">Enable this advertisement</p>
                  </div>
                </label>
                <Input
                  label="Priority (0-100, higher = shown first)"
                  name="priority"
                  type="number"
                  value={formData.priority}
                  onChange={handleChange}
                  min="0"
                  max="100"
                  error={errors.priority}
                />
                <Input
                  label="Start Date"
                  name="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={handleChange}
                />
                <Input
                  label="End Date"
                  name="endDate"
                  type="date"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
            </Card>

            <Card title="Target Pages">
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-3">
                  Leave empty to show on all pages
                </p>
                {targetPageOptions.map(page => (
                  <label key={page.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.targetPages.includes(page.value)}
                      onChange={() => handleTargetPageToggle(page.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{page.label}</span>
                  </label>
                ))}
              </div>
            </Card>

            <Card title="Target Categories">
              <div className="space-y-2">
                <p className="text-xs text-gray-500 mb-3">
                  Leave empty to show on all categories
                </p>
                {categoryOptions.length === 0 && (
                  <p className="text-xs text-gray-400 italic">No categories available</p>
                )}
                {categoryOptions.map(cat => (
                  <label key={cat.id || cat.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.targetCategories.includes(cat.id || cat.value)}
                      onChange={() => handleTargetCategoryToggle(cat.id || cat.value)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{cat.name || cat.label}</span>
                  </label>
                ))}
              </div>
            </Card>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 pt-6 border-t">
          <Button variant="secondary" type="button" onClick={() => navigate('/advertisements')}>
            Cancel
          </Button>
          <Button type="submit" loading={submitting}>
            {isEdit ? 'Update Advertisement' : 'Create Advertisement'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AdvertisementForm;
